import React, { ChangeEvent, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useConfig } from 'msp-integrations'
import { InputProps } from './Input.types'
import { getStyles } from './Input.styles'
import PhoneField from '../PhoneField/PhoneField'
import IconButton from '@molecules/IconButton/IconButton'

const Input = ({
  className,
  id,
  readOnly,
  layout = 'default',
  type = 'text',
  labelBackgroundColor = 'bg-white',
  labelText,
  placeholder,
  isDisabled,
  isRequired,
  iconName,
  hasError,
  errorMessage,
  helperText,
  iconClassName,
  inputClassName,
  onChange,
  onFocus,
  onBlur,
  initialPrefix,
  onUpdatePrefix,
  onKeyDown,
  isDarkMode = false,
  autoComplete,
  ...props
}: InputProps) => {
  const [passwordHidden, setPasswordHidden] = useState(true)
  const config = useConfig()
  const styles = getStyles(config, isDarkMode)
  const isPassword = type === 'password'
  const isHidden = type === 'hidden'
  const isDefaultLayout = layout === 'default'
  const togglePasswordHidden = () => {
    setPasswordHidden(!passwordHidden)
  }
  const { innerref = React.createRef(), ...restOfProps } = props as any

  const handleKeyDown = (e: any) => {
    switch (e.keyCode) {
      case 32: // space
      case 37: // arrow left
      case 38: // arrow up
      case 39: // arrow right
      case 40: // arrow down
        e.stopPropagation()
        break
    }
  }

  return (
    <div
      className={`${
        isHidden
          ? ''
          : twMerge(`${styles.base} ${styles[layout].div} ${className}`)
      }`}
    >
      <div>
        {isDefaultLayout && labelText && (
          <div
            className={twMerge(
              'absolute left-[18px] w-auto px-[6px]',
              isDarkMode ? '-top-[14px]' : '-top-[10px]'
            )}
          >
            <label
              className={twMerge(
                styles[layout].label.base,
                !isDisabled &&
                  hasError &&
                  'text-errorRed group-hover:text-errorRed',
                isRequired && 'after:content-["*"]',
                isDisabled && styles[layout].label.disabled
              )}
              htmlFor={id}
            >
              {labelText}
            </label>
            <div
              className={`absolute left-0 w-full ${
                isDarkMode
                  ? `top-1 h-[18px] rounded-b bg-black`
                  : `top-0 h-[12px] ${labelBackgroundColor}`
              }`}
            />
          </div>
        )}
        {type === 'tel' && (
          <PhoneField
            setPrefix={(prefix: string) =>
              onUpdatePrefix &&
              onUpdatePrefix(String(`+${prefix}-`) || config.phonePREFIX)
            }
            initialPrefix={initialPrefix ?? ''}
          />
        )}
        <input
          className={twMerge(
            'w-full appearance-none',
            styles[layout].input,
            type === 'tel' && 'pl-32',
            isPassword && passwordHidden && 'tracking-3',
            isPassword && (isDefaultLayout ? 'pr-[48px]' : 'pr-[40px]'),
            hasError &&
              !isDisabled &&
              `border-errorRed group-hover:border-errorRed focus:border-errorRed ${
                !isDefaultLayout && 'text-errorRed placeholder-errorRed'
              }`,
            inputClassName && inputClassName
          )}
          id={id}
          disabled={isDisabled}
          type={isPassword ? (passwordHidden ? 'password' : 'text') : type}
          placeholder={placeholder}
          ref={innerref}
          autoComplete={isPassword ? 'new-password' : autoComplete}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onKeyDownCapture={(e) => handleKeyDown(e)}
          onChange={(e: ChangeEvent<HTMLInputElement & { value: string }>) => {
            onChange && onChange(e)
          }}
          readOnly={readOnly}
          {...restOfProps}
        />
        {(isPassword || iconName) && (
          <div
            className={twMerge(
              'absolute top-0 flex w-[28px] items-center justify-end',
              styles[layout].passwordIcon,
              isDisabled &&
                'stroke-machine2-75 group-hover:stroke-machine2-75 pointer-events-none'
            )}
          >
            <IconButton
              className={iconClassName}
              iconName={
                isPassword
                  ? passwordHidden
                    ? 'EyeOn'
                    : 'EyeOff'
                  : iconName || ''
              }
              width={'18px'}
              onClick={() => togglePasswordHidden()}
            />
          </div>
        )}
      </div>
      {!isDisabled && (helperText || errorMessage) && (
        <span
          className={twMerge(
            styles[layout].helperText,
            hasError
              ? 'text-errorRed'
              : config.theme.mspComponents?.input?.textColor
          )}
        >
          {hasError ? errorMessage : helperText}
        </span>
      )}
    </div>
  )
}

export default Input
